/**
 * AdminUsuarios — Lista de usuários cadastrados com status online.
 * Auto-refresh a cada 30s.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, User as UserIcon, Search, RefreshCw, Shield, Truck, ShoppingBag, Wifi, WifiOff, Edit2, Trash2, Key, X, Save, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
};

const timeAgo = (s) => {
  if (!s) return '—';
  try {
    const diff = (Date.now() - new Date(s).getTime()) / 1000;
    if (diff < 60) return 'agora mesmo';
    if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
    return `há ${Math.floor(diff / 86400)} d`;
  } catch { return s; }
};

const ROLE_INFO = {
  admin: { label: 'Admin', icon: Shield, cls: 'text-purple-700 bg-purple-100 dark:bg-purple-900/30' },
  vendedor: { label: 'Vendedor', icon: Truck, cls: 'text-blue-700 bg-blue-100 dark:bg-blue-900/30' },
  cliente: { label: 'Cliente', icon: ShoppingBag, cls: 'text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30' },
};

export default function AdminUsuarios() {
  const [data, setData] = useState({ users: [], total: 0, online_count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [editUser, setEditUser] = useState(null); // user being edited or null
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', must_change_password: false, new_password: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { email, senha }

  const carregar = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`);
      setData(r.data);
    } catch (e) {
      if (!silent) toast.error('Erro ao carregar usuários');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    // Auto-refresh a cada 30s
    const t = setInterval(() => carregar(true), 30000);
    return () => clearInterval(t);
  }, [carregar]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return (data.users || []).filter(u => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (onlyOnline && !u.online) return false;
      if (!term) return true;
      const hay = `${u.name} ${u.email} ${u.role}`.toLowerCase();
      return hay.includes(term);
    });
  }, [data.users, search, roleFilter, onlyOnline]);

  const stats = useMemo(() => {
    const users = data.users || [];
    return {
      total: users.length,
      online: data.online_count || 0,
      admins: users.filter(u => u.role === 'admin').length,
      vendedores: users.filter(u => u.role === 'vendedor').length,
      clientes: users.filter(u => u.role === 'cliente').length,
    };
  }, [data]);

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      role: u.role || 'cliente',
      must_change_password: !!u.must_change_password,
      new_password: '',
    });
    setResetResult(null);
  };
  const closeEdit = () => { setEditUser(null); setResetResult(null); };

  const saveEdit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      const payload = {};
      if (editForm.name && editForm.name !== editUser.name) payload.name = editForm.name;
      if (editForm.email && editForm.email !== editUser.email) payload.email = editForm.email;
      if (editForm.role && editForm.role !== editUser.role) payload.role = editForm.role;
      if (editForm.must_change_password !== !!editUser.must_change_password) payload.must_change_password = editForm.must_change_password;
      if (editForm.new_password && editForm.new_password.length >= 4) payload.new_password = editForm.new_password;
      if (Object.keys(payload).length === 0) { toast.info('Nenhuma alteração feita'); setEditSaving(false); return; }
      await axios.patch(`${API}/auth/users/${editUser.id}`, payload);
      toast.success('Usuário atualizado');
      await carregar();
      closeEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setEditSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!editUser) return;
    if (!window.confirm(`Resetar a senha de ${editUser.name || editUser.email}? Será gerada uma senha temporária que ele deverá trocar no próximo login.`)) return;
    setEditSaving(true);
    try {
      const r = await axios.patch(`${API}/auth/users/${editUser.id}`, { reset_password: true });
      if (r.data?.senha_temporaria) {
        setResetResult({ email: editUser.email, senha: r.data.senha_temporaria });
        toast.success('Senha resetada — copie e entregue ao usuário');
      } else {
        toast.success('Senha resetada');
      }
      await carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao resetar senha');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteUser = async (u) => {
    if (u.role === 'admin') { toast.error('Não é possível remover um admin'); return; }
    if (!window.confirm(`Remover o acesso de ${u.name || u.email}?\n\nIsso desativa o login mas MANTÉM o registro do ${u.role} no sistema.\nO histórico de romaneios/vendas não é afetado.`)) return;
    try {
      await axios.delete(`${API}/auth/users/${u.id}`);
      toast.success('Acesso removido');
      await carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover');
    }
  };

  const copyCreds = async () => {
    if (!resetResult) return;
    const txt = `E-mail: ${resetResult.email}\nSenha temporária: ${resetResult.senha}`;
    try { await navigator.clipboard.writeText(txt); toast.success('Credenciais copiadas'); }
    catch { toast.error('Não foi possível copiar'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-usuarios-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Usuários Cadastrados
          </h1>
          <p className="text-sm text-muted-foreground">
            {stats.total} usuário{stats.total !== 1 ? 's' : ''} • {stats.online} online agora
          </p>
        </div>
        <button
          onClick={() => carregar()}
          disabled={loading}
          className="btn-secondary flex items-center gap-2"
          data-testid="usuarios-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total" value={stats.total} icon={Users} color="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" />
        <KpiCard label="Online" value={stats.online} icon={Wifi} color="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" testid="kpi-online" />
        <KpiCard label="Admins" value={stats.admins} icon={Shield} color="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" />
        <KpiCard label="Vendedores" value={stats.vendedores} icon={Truck} color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" />
        <KpiCard label="Clientes" value={stats.clientes} icon={ShoppingBag} color="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" />
      </div>

      {/* Filtros */}
      <div className="bg-card border border-border rounded-xl p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="usuarios-search"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="input-field text-sm min-w-[150px]"
          data-testid="usuarios-role-filter"
        >
          <option value="">Todos os tipos</option>
          <option value="admin">Admins</option>
          <option value="vendedor">Vendedores</option>
          <option value="cliente">Clientes</option>
        </select>
        <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border cursor-pointer hover:bg-accent">
          <input
            type="checkbox"
            checked={onlyOnline}
            onChange={(e) => setOnlyOnline(e.target.checked)}
            data-testid="usuarios-only-online"
          />
          <span className="text-sm">Só online</span>
        </label>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="text-center py-16">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl" data-testid="usuarios-empty">
          <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Nenhum usuário encontrado com esses filtros.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Usuário</th>
                <th className="text-left px-3 py-2 font-semibold">Tipo</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-left px-3 py-2 font-semibold">Última atividade</th>
                <th className="text-left px-3 py-2 font-semibold">Cadastrado em</th>
                <th className="text-left px-3 py-2 font-semibold">Segurança</th>
                <th className="text-right px-3 py-2 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(u => {
                const role = ROLE_INFO[u.role] || { label: u.role || '—', icon: UserIcon, cls: 'text-gray-700 bg-gray-100' };
                const RoleIcon = role.icon;
                return (
                  <tr key={u.id} className="hover:bg-accent/40" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${u.online ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                          <UserIcon className={`w-4 h-4 ${u.online ? 'text-green-700 dark:text-green-300' : 'text-gray-500'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{u.name || '—'}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${role.cls}`}>
                        <RoleIcon className="w-3 h-3" />
                        {role.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {u.online ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" data-testid={`status-online-${u.id}`}>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                          </span>
                          ONLINE
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                          <WifiOff className="w-3 h-3" />
                          Offline
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      <span className={u.online ? 'text-green-700 dark:text-green-300 font-medium' : 'text-muted-foreground'}>
                        {u.last_seen_at ? timeAgo(u.last_seen_at) : '—'}
                      </span>
                      {u.last_seen_at && (
                        <p className="text-[10px] text-muted-foreground">{fmtDate(u.last_seen_at)}</p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {fmtDate(u.created_at)}
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      {u.totp_enabled && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
                          <Shield className="w-2.5 h-2.5" /> 2FA
                        </span>
                      )}
                      {u.must_change_password && (
                        <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">
                          Trocar senha
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEdit(u)}
                          className="p-1.5 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                          title="Editar usuário / resetar senha"
                          data-testid={`user-edit-${u.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {u.role !== 'admin' && (
                          <button
                            onClick={() => deleteUser(u)}
                            className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                            title="Remover acesso (mantém o cadastro)"
                            data-testid={`user-delete-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground italic text-center">
        🔄 A lista atualiza automaticamente a cada 30 segundos. &ldquo;Online&rdquo; = atividade nos últimos 5 minutos.
      </p>

      {/* Modal de edição */}
      {editUser && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closeEdit} data-testid="user-edit-modal">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-blue-600" />
                Editar Usuário
              </h3>
              <button onClick={closeEdit} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800" data-testid="user-edit-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {resetResult ? (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300 rounded-lg p-3">
                  <p className="text-xs uppercase font-bold text-emerald-800 dark:text-emerald-300 mb-1">Senha temporária gerada</p>
                  <div className="text-sm">
                    <p><b>E-mail:</b> <code className="text-xs">{resetResult.email}</code></p>
                    <p><b>Senha:</b> <code className="text-xs bg-white dark:bg-gray-800 px-2 py-0.5 rounded">{resetResult.senha}</code></p>
                  </div>
                  <button onClick={copyCreds} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline" data-testid="user-copy-creds">
                    <Copy className="w-3 h-3" /> Copiar credenciais
                  </button>
                  <p className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-2 italic">
                    Ao entrar, o usuário será obrigado a trocar essa senha. Copie e entregue agora — depois disso ela não fica visível.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">Nome</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-background"
                      data-testid="user-edit-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">E-mail</label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-background"
                      data-testid="user-edit-email"
                    />
                  </div>
                  {editUser.role !== 'admin' && (
                    <div>
                      <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">Tipo</label>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm(f => ({ ...f, role: e.target.value }))}
                        className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-background"
                        data-testid="user-edit-role"
                      >
                        <option value="vendedor">Vendedor</option>
                        <option value="cliente">Cliente</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                      Nova senha (opcional)
                    </label>
                    <input
                      type="text"
                      value={editForm.new_password}
                      onChange={(e) => setEditForm(f => ({ ...f, new_password: e.target.value }))}
                      placeholder="Deixe em branco para não alterar"
                      className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-background font-mono"
                      data-testid="user-edit-password"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Mín. 4 caracteres. Ou use &ldquo;Resetar senha&rdquo; abaixo para gerar aleatória.</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.must_change_password}
                      onChange={(e) => setEditForm(f => ({ ...f, must_change_password: e.target.checked }))}
                      data-testid="user-edit-must-change"
                    />
                    Forçar troca de senha no próximo login
                  </label>
                </>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
              {!resetResult && (
                <button
                  onClick={resetPassword}
                  disabled={editSaving}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-100 hover:bg-amber-200 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                  data-testid="user-reset-password"
                >
                  <Key className="w-3.5 h-3.5" /> Resetar Senha (gera aleatória)
                </button>
              )}
              <div className={`flex items-center gap-2 ${resetResult ? 'ml-auto' : ''}`}>
                <button onClick={closeEdit} className="px-3 py-1.5 text-xs rounded-md hover:bg-gray-100 dark:hover:bg-gray-800">Fechar</button>
                {!resetResult && (
                  <button
                    onClick={saveEdit}
                    disabled={editSaving}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                    data-testid="user-edit-save"
                  >
                    <Save className="w-3.5 h-3.5" /> Salvar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-xs text-muted-foreground mt-2">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}
